import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, List, ListItem, ListItemText, IconButton, Box, Typography, Grid, ListItemSecondaryAction } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { UsuarioAddRemoveSetorDTO, UsuarioList } from '../../types/usuario/UsuarioList';
import { useGetSetoresQuery } from '../setor/setorApiSlice';
import { Setor, SetorList } from '../../types/setor/Setor';
import TxtSetor from '../autoComplete/TxtSetor';
import { GridDeleteIcon } from '@mui/x-data-grid';
import { useAddSetorUsuarioMutation, useDeleteSetorUsuarioMutation } from './usuarioApiSlice';

interface Props {
  open: boolean;
  onClose: () => void;
  usuario: UsuarioList;
  atualizarUsuarioNaLista: (usuarioAtualizado: UsuarioList) => void;
}

export const DialogSetores = ({ open, onClose, usuario, atualizarUsuarioNaLista }: Props) => {
  const [setores, setSetores] = useState<SetorList[]>([]);
  const [setorSelecionado, setSetorSelecionado] = useState<Setor | null>(null);

  const [addSetorUsuario] = useAddSetorUsuarioMutation();
  //const [addSetorUsuario1] = useAddSetorUsuario1Mutation();
  const [deleteSetorUsuario] = useDeleteSetorUsuarioMutation();

  // Carrega os setores do usuário ao abrir o dialog
  useEffect(() => {
    if (usuario) {
      const setoresComFlags = (usuario.setores ?? []).map(s => ({
        ...s,
        isNovo: false,
        excluido: false
      }));

      setSetores(setoresComFlags);
    }
  }, [usuario]);

  // Manipula a seleção do autocomplete
  const handleTxtSetorChange = (setor: Setor | null) => {
    setSetorSelecionado(setor);
  };

  // Adicionar setor
  const handleAdicionar = () => {
    if (!setorSelecionado?.id) return;

    // evita duplicidade
    if (setores.some(s => s.id === setorSelecionado.id && !s.excluido)) {
      alert('Este setor já foi adicionado!');
      return;
    }
    setSetores(prev => [
      ...prev,
      {
        ...setorSelecionado,
        novo: true,
        excluido: false
      }
    ]);
    setSetorSelecionado(null);
  };

  // Excluir setor
  const handleExcluir = (id: string) => {
    setSetores(
      prev =>
        prev
          .map(s => {
            if (s.id === id) {
              if (s.novo) {
                // setor ainda não estava no banco → remover completamente
                return null;
              }
              // setor que já existia → marcar como excluído
              return { ...s, excluido: true };
            }
            return s;
          })
          .filter(Boolean) as any[]
    );
  };

  // Confirmar alterações
  const handleConfirmar = async () => {
    const setoresNovos: UsuarioAddRemoveSetorDTO[] = setores
      .filter(s => s.novo && !s.excluido)
      .map(s => ({
        idSetor: s.id,
        idUsuario: usuario.id
      }));

    const setoresParaExcluir: UsuarioAddRemoveSetorDTO[] = setores
      .filter(s => !s.novo && s.excluido)
      .map(s => ({
        idSetor: s.id,
        idUsuario: usuario.id
      }));

    const params = {
      idUsuario: usuario.id,
      idSetor: 'teste'
    };

    try {
      //  await addSetorUsuario1(params).unwrap();

      // Adicionar setores novos
      if (setoresNovos.length > 0) {
        await addSetorUsuario(setoresNovos).unwrap();
      }

      // Excluir setores removidos
      if (setoresParaExcluir.length > 0) {
        await deleteSetorUsuario(setoresParaExcluir).unwrap();
      }

      // Atualizar lista local após salvar
      atualizarUsuarioNaLista({
        ...usuario,
        setores
      });

      onClose();
    } catch (error) {
      console.error('Erro ao salvar setores:', error);
      alert('Erro ao salvar setores do usuário.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Setores de {usuario.nome}</DialogTitle>

      <DialogContent>
        <Grid container mt={2} spacing={2} alignItems="center">
          <Grid item xs={9}>
            <FormControl fullWidth>
              <TxtSetor value={setorSelecionado ?? undefined} handleSearchChange={handleTxtSetorChange} />
            </FormControl>
          </Grid>

          <Grid item xs={3}>
            <Button onClick={handleAdicionar} variant="contained" color="primary" fullWidth disabled={!setorSelecionado?.id}>
              Adicionar
            </Button>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" mt={3} mb={1}>
          Setores adicionados:
        </Typography>

        <List dense>
          {setores
            .filter(it => !it.excluido)
            .map(it => {
              let descricao: string = '';

              if (it.nome) {
                descricao = it.nome;
              }

              if (it.secretaria?.orgao?.abreviatura) {
                descricao += ' - ' + it.secretaria.orgao.abreviatura;
              }

              if (it.secretaria?.abreviatura) {
                descricao += ' - ' + it.secretaria.abreviatura;
              }

              return (
                <ListItem key={it.id}>
                  <ListItemText primary={descricao} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleExcluir(it.id)}>
                      <GridDeleteIcon color="secondary" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
        </List>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="contained" onClick={handleConfirmar}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
